import { useState, type ReactNode } from "react"

/**
 * Primitivas de UI. Van juntas en un archivo porque son chicas y cohesivas; los
 * componentes con lógica propia (filtros, páginas, drawer) sí tienen archivo aparte.
 *
 * Sin librería de componentes ni de iconos, igual que calendar-app. Los iconos son SVG
 * inline: calendar-app carga Font Awesome de un CDN y acá no se hace, para no poner un
 * tercero en el camino crítico (y porque el CSP no lo permitiría).
 */

// ── Pills ─────────────────────────────────────────────────────────────────────

/**
 * El TEXTO siempre está presente; el color solo refuerza. Los tres tonos semánticos de
 * la marca no son distinguibles con daltonismo cuando se tocan, así que el estado nunca
 * se codifica SOLO por color.
 */
export function StatusPill({ status, label }: { status: string; label: string }) {
  return <span className={`pill pill-${status}`}>{label}</span>
}

export function Pill({
  children,
  variant = "neutral",
  title,
}: {
  children: ReactNode
  variant?: "neutral" | "accent" | "approved" | "not_approved" | "pending"
  title?: string
}) {
  return (
    <span className={`pill pill-${variant}`} title={title}>
      {children}
    </span>
  )
}

// ── Estados ───────────────────────────────────────────────────────────────────

export function EmptyState({
  title,
  hint,
  action,
}: {
  title: string
  hint?: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="state-block">
      <div className="state-title">{title}</div>
      {hint && <div>{hint}</div>}
      {action && <div className="state-actions">{action}</div>}
    </div>
  )
}

export function ErrorState({
  title = "Algo salió mal",
  message,
  onRetry,
}: {
  title?: string
  message: string
  onRetry?: () => void
}) {
  return (
    <div className="state-block state-error">
      <div className="state-title">{title}</div>
      <div>{message}</div>
      {onRetry && (
        <div className="state-actions">
          <button type="button" className="btn" onClick={onRetry}>
            Reintentar
          </button>
        </div>
      )}
    </div>
  )
}

/** Filas fantasma para la PRIMERA carga. En los refetch se sostiene la tabla anterior. */
export function SkeletonRows({ rows, cols }: { rows: number; cols: number }) {
  return (
    <>
      {Array.from({ length: rows }, (_, r) => (
        <tr key={r} className="skeleton-row">
          {Array.from({ length: cols }, (_, c) => (
            <td key={c}>
              <div className="skeleton-bar" style={{ width: `${45 + ((r + c) % 5) * 11}%` }} />
            </td>
          ))}
        </tr>
      ))}
    </>
  )
}

// ── Copiar al portapapeles ────────────────────────────────────────────────────

/**
 * El DM de aprobación muestra el id de la solicitud, así que copiarlo es LA acción de
 * cruce entre el tablero y Slack.
 */
export function CopyButton({ value, label = "Copiar" }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false)

  async function copy() {
    try {
      await navigator.clipboard.writeText(value)
    } catch {
      // Fallback para contextos sin permiso de clipboard.
      const ta = document.createElement("textarea")
      ta.value = value
      ta.style.position = "fixed"
      ta.style.opacity = "0"
      document.body.appendChild(ta)
      ta.select()
      document.execCommand("copy")
      document.body.removeChild(ta)
    }
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1500)
  }

  return (
    <button type="button" className="btn btn-ghost btn-sm" onClick={() => void copy()}>
      {copied ? "Copiado ✓" : label}
    </button>
  )
}

// ── Header ordenable ──────────────────────────────────────────────────────────

export function SortHeader({
  label,
  field,
  activeField,
  dir,
  onSort,
  className,
}: {
  label: string
  field: string
  activeField: string
  dir: "asc" | "desc"
  onSort: (field: string) => void
  className?: string
}) {
  const active = activeField === field
  return (
    <th className={className} aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}>
      <button type="button" className="sort-header" onClick={() => onSort(field)}>
        {label}
        {active && <span className="sort-arrow">{dir === "asc" ? "▲" : "▼"}</span>}
      </button>
    </th>
  )
}

// ── Paginación ────────────────────────────────────────────────────────────────

/**
 * "Cargar más" explícito con el total a la vista. Una tabla de AUDITORÍA nunca debe
 * usar scroll infinito: hay que poder decir "revisé las 48".
 */
export function Pagination({
  shown,
  total,
  onMore,
  busy,
}: {
  shown: number
  total: number
  onMore: () => void
  busy: boolean
}) {
  return (
    <div className="table-footer">
      <span>
        Mostrando {shown} de {total}
      </span>
      {shown < total && (
        <button type="button" className="btn" onClick={onMore} disabled={busy}>
          {busy ? "Cargando…" : "Cargar más"}
        </button>
      )}
    </div>
  )
}
